import { Component, EventEmitter, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { BenefitCheckoutWizardPanel } from 'src/app/constants/wizard-panels';
import { BeneficiariesResponse, BeneficiaryPayload } from 'src/app/models/beneficiary';
import { BenefitSummaryList } from 'src/app/models/benefit-summary';
import { DocumentPayload } from 'src/app/models/document';
import { CheckoutAdminEvent, CheckoutComplete, CheckoutDependents, CheckoutDependentsPayload, CheckoutDocuments, CheckoutFinalize, CheckoutSpousalExclusion, CheckoutExclusionPayload, CheckoutSurcharge, CheckoutSurchargePayload, CheckoutWaiveDocument, WizardCoverageSummary } from 'src/app/models/enrollment-checkout';
import { NextPanel } from 'src/app/models/next-panel';
import { USAePay, USAePayPayload } from 'src/app/models/USAePay';
import { EnrollmentCheckoutService } from 'src/app/services/enrollment-checkout.service';
import { EnrollmentManagerService } from 'src/app/services/enrollment-manager.service';
import { FileUploadService } from 'src/app/services/file-upload.service';
import { VoyaSSO } from "src/app/models/voya";
import { VoyaService } from "src/app/services/voya.service";

@Component({
    selector: 'mwp-benefit-enrollment-checkout',
    templateUrl: './wizard-checkout.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BenefitEnrollmentCheckoutComponent implements OnInit {
    @Output() public emitIntializeComplete: EventEmitter<any> = new EventEmitter();

    public checkoutWaiveDocument: CheckoutWaiveDocument;
    public checkoutDependents: CheckoutDependents;
    public checkoutAdminEvent: CheckoutAdminEvent;
    public summaryBenefitList: BenefitSummaryList;
    public beneficiariesResponse: BeneficiariesResponse;
    public checkoutDocuments: CheckoutDocuments;
    public usaEPay: USAePay;
    public checkoutFinalize: CheckoutFinalize;
    public wizardCoverageSummary: WizardCoverageSummary;
    public checkoutSurcharge: CheckoutSurcharge;
    public checkoutSpousalExclusion: CheckoutSpousalExclusion;

    public displayWaiveDocumentPanel: boolean;
    public displaySSNPanel: boolean;
    public displayDependentsVerify: boolean;
    public displaySurchargePanel: boolean;
    public displayBeneficiariePanel: boolean;
    public displayUSAePayPanel: boolean;
    public displayDocumentsPanel: boolean;
    public displayAutoEnrollPanel: boolean;
    public displayAdminEventPanel: boolean;
    public displaySummaryPanel: boolean;
    public displayFinalizePanel: boolean;
    public displaySpousalExclusionPanel: boolean;


    public isLoading: boolean;
    public isNotConfigured: boolean;
    private genericPayload: any;

    constructor(
        public enrollmentManagerService: EnrollmentManagerService,
        private enrollmentCheckoutService: EnrollmentCheckoutService,
        private fileUploadService: FileUploadService,
        private voyaService: VoyaService,
    ) {
        this.enrollmentManagerService.resetEnrollmentStepper();
        this.enrollmentManagerService.$GoBackEnrollmentStep.subscribe(() => {
            if (this.enrollmentManagerService.enrollmentStepper) {
                this.isLoading = true;
                this.resetPanels();
                let l_NextPanel = new NextPanel();
                l_NextPanel.nextPanel.value = this.enrollmentManagerService.benefitWizardControlPanel.panel;
                this.getPanel(l_NextPanel);
            }
        });
    }

    ngOnInit(): void {
        this.intializePanel();
    }

    private intializePanel(): void {
        this.resetPanels();
        this.isLoading = true;
        this.enrollmentCheckoutService.initializeSerivce().subscribe((response: NextPanel) => {
            this.emitIntializeComplete.emit();
            this.getPanel(response);
        });
    }

    private resetPanels(): void {
        this.checkoutWaiveDocument =
            this.checkoutSurcharge =
            this.checkoutDependents =
            this.wizardCoverageSummary =
            this.checkoutDocuments =
            this.beneficiariesResponse =
            this.usaEPay =
            this.summaryBenefitList =
            this.checkoutAdminEvent =
            this.checkoutFinalize = null;

        this.isLoading =
            this.displaySSNPanel =
            this.displayDependentsVerify =
            this.displaySurchargePanel =
            this.displayAutoEnrollPanel =
            this.displayBeneficiariePanel =
            this.displaySpousalExclusionPanel = 
            this.displayUSAePayPanel =
            this.displayDocumentsPanel =
            this.displayWaiveDocumentPanel =
            this.displayAdminEventPanel =
            this.displaySummaryPanel =
            this.displayFinalizePanel =
            this.isNotConfigured = false;
    }

    private resetPanelsAndUpdateStepper(): void {
        this.resetPanels();
        this.enrollmentManagerService.updateEnrollmentStepper();
    }

    private setError() {
        this.isNotConfigured = true;
    }

    public getPanel(nextPanelResponse: NextPanel): void {
        this.enrollmentManagerService.benefitWizardControlPanel.panel = nextPanelResponse.nextPanel.value;

        switch (this.enrollmentManagerService.benefitWizardControlPanel.panel) {
            case BenefitCheckoutWizardPanel.adminEvent:
                this.enrollmentCheckoutService.getAdminEventNotice().subscribe((response: CheckoutAdminEvent) => {
                    this.resetPanelsAndUpdateStepper();
                    this.checkoutAdminEvent = response;
                    this.displayAdminEventPanel = true;
                });
                break;

            case BenefitCheckoutWizardPanel.dependentSSN:
                this.enrollmentCheckoutService.getDependentSSN().subscribe((response: CheckoutDependents) => {
                    this.resetPanelsAndUpdateStepper();
                    this.checkoutDependents = response;
                    this.displaySSNPanel = true;
                });
                break;

            case BenefitCheckoutWizardPanel.smokerSurcharge:
                this.enrollmentCheckoutService.getSmokerSurcharge().subscribe((response: CheckoutSurcharge) => {
                    this.resetPanelsAndUpdateStepper();
                    this.checkoutSurcharge = response;
                    this.displaySurchargePanel = true;
                });
                break;

            case BenefitCheckoutWizardPanel.spouseSurcharge:
                this.enrollmentCheckoutService.getSpousalSurcharge().subscribe((response: CheckoutSurcharge) => {
                    this.resetPanelsAndUpdateStepper();
                    this.checkoutSurcharge = response;
                    this.displaySurchargePanel = true;
                });
                break;

            case BenefitCheckoutWizardPanel.spouseExclusion:
                this.enrollmentCheckoutService.getSpousalExclusion().subscribe((response: CheckoutSpousalExclusion) => {
                    this.resetPanelsAndUpdateStepper();
                    this.checkoutSpousalExclusion = response;
                    this.displaySpousalExclusionPanel = true;
                });
                break;

            case BenefitCheckoutWizardPanel.beneficiaries:
                this.enrollmentCheckoutService.getBeneficiaries().subscribe((response: BeneficiariesResponse) => {
                    this.resetPanelsAndUpdateStepper();
                    this.beneficiariesResponse = response;
                    this.displayBeneficiariePanel = true;
                });
                break;

            case BenefitCheckoutWizardPanel.dependentVerify:
                this.enrollmentCheckoutService.getDependentVerify().subscribe((response: CheckoutDependents) => {
                    this.resetPanelsAndUpdateStepper();
                    this.checkoutDependents = response;
                    this.displayDependentsVerify = true;
                });
                break;

            case BenefitCheckoutWizardPanel.usaePay:
                this.enrollmentCheckoutService.getUSAePay().subscribe((response: USAePay) => {
                    this.resetPanelsAndUpdateStepper();
                    this.usaEPay = response;
                    this.displayUSAePayPanel = true;
                });
                break;

            case BenefitCheckoutWizardPanel.autoEnroll:
                this.enrollmentCheckoutService.getAutoEnroll().subscribe((response: WizardCoverageSummary) => {
                    this.resetPanelsAndUpdateStepper();
                    this.wizardCoverageSummary = response;
                    this.displayAutoEnrollPanel = true;
                });
                break;


            case BenefitCheckoutWizardPanel.documents:
                this.enrollmentCheckoutService.getDocuments().subscribe((response: CheckoutDocuments) => {
                    this.resetPanelsAndUpdateStepper();
                    this.checkoutDocuments = response;
                    this.displayDocumentsPanel = true;
                });
                break;

            case BenefitCheckoutWizardPanel.waiveDocuments:
                this.enrollmentCheckoutService.getWaiveDocuments().subscribe((response: CheckoutWaiveDocument) => {
                    this.resetPanelsAndUpdateStepper();
                    this.checkoutWaiveDocument = response;
                    this.displayWaiveDocumentPanel = true;
                });
                break;

            case BenefitCheckoutWizardPanel.summary:
                this.enrollmentCheckoutService.getSummary().subscribe((response: BenefitSummaryList) => {
                    this.resetPanelsAndUpdateStepper();
                    this.summaryBenefitList = response;
                    this.displaySummaryPanel = true;
                });
                break;

            case BenefitCheckoutWizardPanel.finalize:
                this.enrollmentCheckoutService.getFinalize().subscribe((response: CheckoutFinalize) => {
                    this.resetPanelsAndUpdateStepper();
                    this.checkoutFinalize = response;
                    this.displayFinalizePanel = true;
                });
                break;

            case BenefitCheckoutWizardPanel.complete:
                this.enrollmentCheckoutService.getComplete().subscribe((response: CheckoutComplete) => {
                    this.enrollmentManagerService.$EnrollmentComplete.next(response);
                });
                break;

            case BenefitCheckoutWizardPanel.pcnaDependent:
            default:
                // PcnaDependent logic is commented out in the API
                this.resetPanels();
                this.setError();
                break;
        }
    }

    public completePanel($payload: any): void {
        this.genericPayload = $payload;

        switch (this.enrollmentManagerService.benefitWizardControlPanel.panel) {
            case BenefitCheckoutWizardPanel.adminEvent:
                this.enrollmentCheckoutService.submitAdminEventNotice().subscribe((response: NextPanel) => {
                    this.getPanel(response);
                });
                break;

            case BenefitCheckoutWizardPanel.dependentSSN:
                const l_CheckoutDependentsPayload = this.genericPayload as CheckoutDependentsPayload;
                this.enrollmentCheckoutService.submitDependentSSN(l_CheckoutDependentsPayload).subscribe((response: NextPanel) => {
                    this.getPanel(response);
                });
                break;

            case BenefitCheckoutWizardPanel.smokerSurcharge:
                const l_CheckoutSurchargePayload = this.genericPayload as CheckoutSurchargePayload;
                this.enrollmentCheckoutService.submitSmokerSurcharge(l_CheckoutSurchargePayload).subscribe((response: NextPanel) => {
                    this.getPanel(response);
                });
                break;

            case BenefitCheckoutWizardPanel.spouseSurcharge:
                const l_CheckoutSurchargePayload2 = this.genericPayload as CheckoutSurchargePayload;
                this.enrollmentCheckoutService.submitSpousalSurcharge(l_CheckoutSurchargePayload2).subscribe((response: NextPanel) => {
                    this.getPanel(response);
                });
                break;

            case BenefitCheckoutWizardPanel.spouseExclusion:
                const l_CheckoutExclusionPayload: CheckoutExclusionPayload = this.genericPayload as CheckoutExclusionPayload;;
                this.enrollmentCheckoutService.submitSpousalExclusion(l_CheckoutExclusionPayload).subscribe((response: NextPanel) => {
                    this.getPanel(response);
                });
                break;

            case BenefitCheckoutWizardPanel.beneficiaries:
                const l_BeneficiaryPayload = this.genericPayload as BeneficiaryPayload;
                this.enrollmentCheckoutService.submitBeneficiaries(l_BeneficiaryPayload).subscribe((response: NextPanel) => {
                    this.getPanel(response);
                });
                break;

            case BenefitCheckoutWizardPanel.dependentVerify:
                this.enrollmentCheckoutService.submitDependentVerify().subscribe((response: NextPanel) => {
                    this.getPanel(response);
                });
                break;

            case BenefitCheckoutWizardPanel.usaePay:
                const l_USAePayPayload = this.genericPayload as USAePayPayload;
                this.enrollmentCheckoutService.submitUSAePay(l_USAePayPayload).subscribe((response: NextPanel) => {
                    this.getPanel(response);
                });
                break;

            case BenefitCheckoutWizardPanel.autoEnroll:
                this.enrollmentCheckoutService.submitAutoEnroll().subscribe((response: NextPanel) => {
                    this.getPanel(response);
                });
                break;

            case BenefitCheckoutWizardPanel.documents:
                this.enrollmentCheckoutService.submitDocuments().subscribe((response: NextPanel) => {
                    this.getPanel(response);
                });
                break;

            case BenefitCheckoutWizardPanel.waiveDocuments:
                const l_Payload = new DocumentPayload();
                l_Payload.files = this.genericPayload as File[];
                this.fileUploadService.submitCheckoutWaiveDocuments(l_Payload).subscribe((response: NextPanel) => {
                    this.getPanel(response);
                });
                break;

            case BenefitCheckoutWizardPanel.summary:
                this.enrollmentCheckoutService.submitSummary().subscribe((response: NextPanel) => {
                    this.getPanel(response);
                });
                break;

            case BenefitCheckoutWizardPanel.finalize:
                if (this.checkoutFinalize.showVOYAEoi) {
                    this.voyaService.getVoyaSSO().subscribe((response: VoyaSSO) => {
                        var tab = window.open('', '_blank');
                        tab.document.write(response.content);
                        tab.document.close();
                    });
                }

                this.enrollmentCheckoutService.submitFinalize().subscribe((response: NextPanel) => {
                    this.getPanel(response);
                });
                break;
            
            default:
                this.resetPanels();
                this.setError();
                break;
        }

    }
}